import { useState } from 'react';
import { Link } from 'react-router';
import { Copy, Monitor, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@staff-app/ui';
import type { CustomerResolveResult, DeviceSearchResult } from '@staff-app/shared-types';
import { CustomerSearchBox } from './CustomerSearchBox';
import { CustomerPanel } from './CustomerPanel';
import { useStaffSearchDevices } from '../hooks/useStaffCustomer';
import { useCustomersSearchStore } from '../stores/customers-search-store';

function devicesLink(accountId: string, uuid?: string): string {
  const base = `/staff/customers/${encodeURIComponent(accountId)}/devices`;
  if (!uuid) return base;
  const params = new URLSearchParams({ deviceUUID: uuid });
  return `${base}?${params.toString()}`;
}

/** Small inline copy-to-clipboard button, matching the Users-card pattern. */
function CopyButton({ value, label, testId }: { value: string; label: string; testId?: string }) {
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          toast.success(`${label} copied`);
        } catch {
          toast.error('Copy failed');
        }
      }}
      aria-label={`Copy ${label}`}
      title={`Copy ${label}`}
      data-testid={testId}
      className="ml-1.5 inline-flex cursor-pointer items-center align-middle hover:text-foreground"
    >
      <Copy className="h-3 w-3" />
    </button>
  );
}

/**
 * Devices column — search by device name (substring) or by exact UUID,
 * device_id (`_id`), or pairing code. Backend matches all in one query.
 */
export function DevicesSearchColumn() {
  // Query is cached in a store so it survives navigating away and back, until
  // the user clears it or searches something else.
  const query = useCustomersSearchStore((s) => s.devicesQuery);
  const setQuery = useCustomersSearchStore((s) => s.setDevicesQuery);
  const { data, loading, error } = useStaffSearchDevices(query);
  const results = data?.staffSearchDevices ?? [];

  // Clicking an account name opens the customer info side panel, same as the
  // Users column. We only have the device's account fields here (id / name /
  // owner), so build a minimal CustomerResolveResult — CustomerPanel fetches
  // INFO / history / notes itself by accountId.
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelResolved, setPanelResolved] = useState<CustomerResolveResult | null>(null);

  const openPanel = (r: DeviceSearchResult) => {
    setPanelResolved({
      found: true,
      inputEmail: r.ownerEmail ?? r.accountId,
      ownerResolvable: true,
      accountId: r.accountId,
      companyName: r.companyName,
      ownerEmail: r.ownerEmail,
      inputIsOwner: true,
      teams: [],
    });
    setPanelOpen(true);
  };

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border bg-card p-4"
      data-testid="devices-search-column"
    >
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Monitor className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">{'Devices'}</h2>
        </div>
        {query ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 gap-1 text-xs"
            onClick={() => setQuery('')}
            data-testid="devices-search-clear"
          >
            <X className="h-3 w-3" />
            {'Clear'}
          </Button>
        ) : null}
      </header>
      <CustomerSearchBox
        initialValue={query}
        onSubmit={setQuery}
        placeholder={'Name, UUID, device ID, or pairing code'}
        ariaLabel={'Search devices'}
        testId="devices-search-box"
      />
      {error ? (
        <p className="text-xs text-destructive" data-testid="devices-search-error">
          {'Search failed.'} {error.message}
        </p>
      ) : loading ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{'Searching…'}</p>
      ) : query.trim().length < 2 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{'Type to search…'}</p>
      ) : results.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{'No results.'}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{'Device'}</TableHead>
              <TableHead>{'Account'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.map((r) => (
              <TableRow
                key={r.deviceId}
                className="hover:bg-accent/50 transition-colors"
                data-testid="devices-search-result-row"
              >
                <TableCell className="align-top text-xs text-muted-foreground">
                  <Link
                    to={devicesLink(r.accountId, r.UUID)}
                    className="text-sm font-medium text-primary cursor-pointer underline-offset-2 hover:underline"
                  >
                    {r.deviceName ?? '—'}
                  </Link>
                  <div>
                    <span className="text-foreground">{'Device id: '}</span>
                    {r.deviceId}
                  </div>
                  <div>
                    <span className="text-foreground">{'UUID: '}</span>
                    {r.UUID ?? r.deviceId}
                  </div>
                  {r.pairingCode ? (
                    <div>
                      <span className="text-foreground">{'Pairing Code: '}</span>
                      {r.pairingCode}
                    </div>
                  ) : null}
                  <div data-testid="devices-search-team">
                    <span className="text-foreground">{'Team: '}</span>
                    {r.teamName ?? 'Default'}
                  </div>
                </TableCell>
                <TableCell className="align-top text-xs text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => openPanel(r)}
                    className="text-left text-sm font-medium text-primary cursor-pointer underline-offset-2 hover:underline"
                    data-testid="devices-search-account-name"
                  >
                    {r.companyName ?? r.accountId}
                  </button>
                  {r.ownerEmail ? (
                    <div>
                      <span className="text-foreground">{'Owner: '}</span>
                      {r.ownerEmail}
                      <CopyButton
                        value={r.ownerEmail}
                        label="Owner email"
                        testId="devices-search-copy-owner"
                      />
                    </div>
                  ) : null}
                  <div>
                    <span className="text-foreground">{'Account id: '}</span>
                    {r.accountId}
                    <CopyButton
                      value={r.accountId}
                      label="Account id"
                      testId="devices-search-copy-account-id"
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <CustomerPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        resolved={panelResolved}
      />
    </section>
  );
}
